import {
	Filler,
	LineElement,
	PointElement,
	Chart as PolygonChart,
	RadialLinearScale,
	type TooltipItem,
	type TooltipModel,
	Tooltip as TooltipPlugin,
} from "chart.js";
import React, { useState } from "react";
import { Radar } from "react-chartjs-2";

import { Text, Tooltip } from "@performyard/py-components";
import { Competencies } from "@performyard/service-contracts";

import { useIsTextClipped } from "../../../../hooks/useIsTextClipped";

import { getColor } from "../../../../utils/getColor";

PolygonChart.register(RadialLinearScale, PointElement, LineElement, Filler, TooltipPlugin);

export type CompetencyAxis = Competencies["company_wide"][number];

type CompetencyRadarProps = {
	title: string;
	charts: CompetencyAxis[][];
	fullWidth?: boolean;
};

// Below this a polygon has no area, so the group degrades to a plain label/level list.
const MIN_RADAR_AXES = 3;

// Geometry mirrors the Figma radar component (Employee Insights / PY-12606): a 160px plot
// ring centered in a 200px-tall block, whose axis labels are HTML siblings anchored 8px
// beyond their vertex — single-line, ellipsized by the browser at the block edge — rather
// than canvas text, which chart.js clips instead of truncating.
const RING_RADIUS = 80;
const LABEL_GAP = 8;
// Padding keeps ring-vertex points (2px dots, 4px on hover) inside the canvas.
const CANVAS_PADDING = 8;
const CANVAS_SIZE = RING_RADIUS * 2 + CANVAS_PADDING * 2;
const LABEL_MAX_WIDTH = 76;
const LABEL_HEIGHT = 16;
const CHART_BLOCK_MAX_WIDTH = RING_RADIUS * 2 + (LABEL_GAP + LABEL_MAX_WIDTH) * 2;

const formatLevel = (value: number | null): string => (value === null ? "—" : `${value}`);

// 6-digit hex + "4D" = 30% opacity, so overlapping polygons stay readable.
const withAlpha = (hex: string): string => `${hex}4D`;

type PointTooltip = {
	x: number;
	y: number;
	lines: string[];
};

// What the canvas tooltip used to print: the series name, then the competency and its level.
// A point with no level plots nothing, so it contributes nothing here either.
const buildTooltipLines = (dataPoints: TooltipItem<"radar">[]): string[] =>
	dataPoints.flatMap((point) =>
		point.raw === null || point.raw === undefined
			? []
			: [point.dataset.label ?? "", `${point.label} (${point.formattedValue})`]
	);

const readPointTooltip = (tooltip: TooltipModel<"radar">): PointTooltip | null => {
	if (tooltip.opacity === 0) {
		return null;
	}
	const lines = buildTooltipLines(tooltip.dataPoints);
	return lines.length === 0 ? null : { x: tooltip.caretX, y: tooltip.caretY, lines };
};

const isSamePointTooltip = (a: PointTooltip | null, b: PointTooltip | null): boolean =>
	a === b ||
	(a !== null &&
		b !== null &&
		a.x === b.x &&
		a.y === b.y &&
		a.lines.length === b.lines.length &&
		a.lines.every((line, index) => line === b.lines[index]));

type PointLabelPlacement = {
	side: "top" | "bottom" | "left" | "right";
	x: number;
	y: number;
};

// chart.js draws axis 0 straight up and proceeds clockwise. A vertical vertex anchors its
// label above/below the ring; every other label grows sideways from its vertex toward the
// block edge.
const placePointLabels = (count: number): PointLabelPlacement[] =>
	Array.from({ length: count }, (_, index) => {
		const angle = -Math.PI / 2 + (index * 2 * Math.PI) / count;
		const x = RING_RADIUS * Math.cos(angle);
		const y = RING_RADIUS * Math.sin(angle);
		if (Math.abs(x) < 1) {
			return { side: y < 0 ? ("top" as const) : ("bottom" as const), x: 0, y: 0 };
		}
		return {
			side: x > 0 ? ("right" as const) : ("left" as const),
			x: Math.round(Math.abs(x) + LABEL_GAP),
			y: Math.round(y),
		};
	});

// Side labels get `width` up to their box's edge so the ellipsis lands at the true edge,
// with `maxWidth` capping them at the Figma's 76px when there is more room than that.
const pointLabelStyle = ({ side, x, y }: PointLabelPlacement): React.CSSProperties => {
	switch (side) {
		case "top":
			return {
				left: "50%",
				transform: "translateX(-50%)",
				bottom: `calc(50% + ${RING_RADIUS + LABEL_GAP}px)`,
				maxWidth: LABEL_MAX_WIDTH,
			};
		case "bottom":
			return {
				left: "50%",
				transform: "translateX(-50%)",
				top: `calc(50% + ${RING_RADIUS + LABEL_GAP}px)`,
				maxWidth: LABEL_MAX_WIDTH,
			};
		case "right":
			return {
				left: `calc(50% + ${x}px)`,
				top: `calc(50% + ${y - LABEL_HEIGHT / 2}px)`,
				width: `calc(50% - ${x}px)`,
				maxWidth: LABEL_MAX_WIDTH,
				textAlign: "left",
			};
		case "left":
			return {
				right: `calc(50% + ${x}px)`,
				top: `calc(50% + ${y - LABEL_HEIGHT / 2}px)`,
				width: `calc(50% - ${x}px)`,
				maxWidth: LABEL_MAX_WIDTH,
				textAlign: "right",
			};
	}
};

const deriveMax = (axes: CompetencyAxis[]): number | undefined => {
	const values = axes
		.flatMap((axis) => [axis.current, axis.expected])
		.filter((value): value is number => value !== null);
	if (values.length === 0) {
		return undefined;
	}
	return Math.ceil(Math.max(...values));
};

const RadarCard: React.FC<{ title: string; children: React.ReactNode; testId: string; className?: string }> = ({
	title,
	children,
	testId,
	className = "",
}) => (
	<div className={`flex flex-col gap-2 ${className}`.trim()} data-testid={testId}>
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="h3">
			{title}
		</Text>
		{children}
	</div>
);

const LegendChip: React.FC<{ label: string; className: string }> = ({ label, className }) => (
	<div className={`flex items-center justify-center rounded p-1 ${className}`}>
		<Text size="extra-small" weight="semi-bold" color="neutral-9" wrap={false}>
			{label}
		</Text>
	</div>
);

const AxisList: React.FC<{ axes: CompetencyAxis[] }> = ({ axes }) => (
	<ul className="flex flex-col gap-1" data-testid="competency-radar-fallback">
		{axes.map((axis) => (
			<li key={axis.label} className="flex justify-between gap-4" data-testid="competency-radar-fallback-row">
				<Text size="paragraph-3" color="neutral-9">
					{axis.label}
				</Text>
				<Text size="paragraph-3" color="neutral-5">
					{formatLevel(axis.current)} / {formatLevel(axis.expected)}
				</Text>
			</li>
		))}
	</ul>
);

// Deliberately not the app-wide ClampedTextTooltipManager, which hardcodes `align="start"`: these
// labels are centred or right-aligned, so a tooltip anchored to their left edge overhangs badly at
// the 76px cap. Driving the same Tooltip the points use keeps both hovers identical. Once the
// manager takes a placement hint (PY-13806) this can rejoin it and lose the hook.
const AxisLabel: React.FC<{ label: string; placement: PointLabelPlacement }> = ({ label, placement }) => {
	const { ref, isClipped } = useIsTextClipped<HTMLSpanElement>(label);

	return (
		<Tooltip asChild placement="top" content={label} disabled={!isClipped}>
			<span
				ref={ref}
				className="absolute truncate text-caption text-neutral-9"
				style={pointLabelStyle(placement)}
				data-testid="competency-radar-axis-label"
			>
				{label}
			</span>
		</Tooltip>
	);
};

const AxisRadar: React.FC<{ axes: CompetencyAxis[] }> = ({ axes }) => {
	const suggestedMax = deriveMax(axes);
	const placements = placePointLabels(axes.length);
	const [pointTooltip, setPointTooltip] = useState<PointTooltip | null>(null);

	// chart.js replays the last hover on updates and resizes, so an unchanged value must not become
	// a new object — that would re-render the chart for nothing.
	const showPointTooltip = (next: PointTooltip | null): void =>
		setPointTooltip((current) => (isSamePointTooltip(current, next) ? current : next));

	return (
		<div className="relative mx-auto h-[200px] w-full" style={{ maxWidth: CHART_BLOCK_MAX_WIDTH }}>
			<div
				className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
				style={{ height: CANVAS_SIZE, width: CANVAS_SIZE }}
			>
				<Radar
					data={{
						labels: axes.map((axis) => axis.label),
						datasets: [
							{
								label: "Current",
								data: axes.map((axis) => axis.current),
								borderColor: getColor("green", 5),
								borderDash: [4, 4],
								backgroundColor: withAlpha(getColor("green", 5)),
								pointBackgroundColor: getColor("green", 5),
								pointBorderColor: getColor("green", 5),
								borderWidth: 1,
								pointRadius: 2,
								pointHoverRadius: 4,
								clip: false as const,
							},
							{
								label: "Expected",
								data: axes.map((axis) => axis.expected),
								borderColor: getColor("primary", 5),
								backgroundColor: withAlpha(getColor("primary", 5)),
								pointBackgroundColor: getColor("primary", 5),
								pointBorderColor: getColor("primary", 5),
								borderWidth: 1,
								pointRadius: 2,
								pointHoverRadius: 4,
								clip: false as const,
							},
						],
					}}
					options={{
						responsive: true,
						maintainAspectRatio: false,
						spanGaps: true,
						animation: {
							duration: 0.2,
						},
						layout: {
							padding: CANVAS_PADDING,
						},
						plugins: {
							legend: {
								display: false,
							},
							// The canvas draws no tooltip of its own; the hover drives the shared Tooltip
							// below, so point hovers and truncated-label hovers are the same component.
							tooltip: {
								enabled: false,
								external: ({ tooltip }) => showPointTooltip(readPointTooltip(tooltip)),
							},
						},
						scales: {
							r: {
								suggestedMin: 0,
								suggestedMax,
								angleLines: {
									display: false,
								},
								grid: {
									color: getColor("neutral", 2),
									lineWidth: 1,
								},
								pointLabels: {
									display: false,
								},
								ticks: {
									stepSize: 1,
									display: false,
								},
							},
						},
					}}
				/>
				{pointTooltip ? (
					<Tooltip
						open
						asChild
						placement="top"
						content={pointTooltip.lines.map((line, index) => (
							<div key={index}>{line}</div>
						))}
					>
						{/* A zero-size anchor at the caret centres the tooltip over the point, and takes no
						pointer events so it cannot intercept the hover that produced it. */}
						<span
							className="pointer-events-none absolute"
							style={{ left: pointTooltip.x, top: pointTooltip.y }}
							data-testid="competency-radar-point-anchor"
						/>
					</Tooltip>
				) : null}
			</div>
			{axes.map((axis, index) => (
				<AxisLabel key={axis.label} label={axis.label} placement={placements[index]} />
			))}
		</div>
	);
};

export const CompetencyRadar: React.FC<CompetencyRadarProps> = ({ title, charts, fullWidth = false }) => {
	if (charts.length === 0) {
		return null;
	}

	const isSplit = charts.length > 1;
	const plotsAnyRadar = charts.some((axes) => axes.length >= MIN_RADAR_AXES);

	return (
		<RadarCard title={title} testId="competency-radar" className={fullWidth ? "md:col-span-2" : ""}>
			{plotsAnyRadar ? (
				<div className="flex gap-2">
					<LegendChip label="Current" className="bg-green-2" />
					<LegendChip label="Expected" className="bg-primary-1" />
				</div>
			) : null}
			<div className={`grid grid-cols-1 gap-4 ${isSplit ? "md:grid-cols-2" : ""}`}>
				{charts.map((axes, index) => (
					<div key={index} data-testid="competency-radar-chart">
						{axes.length < MIN_RADAR_AXES ? <AxisList axes={axes} /> : <AxisRadar axes={axes} />}
					</div>
				))}
			</div>
		</RadarCard>
	);
};
